import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { isPinataUnoptimizable, markPinataUnoptimizable, stripPinataImageParams } from '../utils/pinata-thumbnail.util';

/**
 * Class carried by an image until it has loaded, styled globally as a shimmer.
 *
 * The media gateway can take a second or two before it starts sending an image, and the box
 * sat blank for all of it. The shimmer shows the image is on its way.
 */
export const IMAGE_SHIMMER_CLASS = 'img-shimmer';

/**
 * Standard treatment for an NFT <img>: a shimmer while it loads, and the original file when the
 * gateway cannot optimize it.
 *
 * **Shimmer.** Set until the image has loaded, and again whenever the bound URL changes - the
 * same element is reused as rows page or a preview switches files. Skipped for an image the
 * browser already has, so a cached image never flickers.
 *
 * **Original when optimizing fails.** The gateway optimizer rejects some formats (AVIF answers
 * 415), while the plain URL serves the file, and the format cannot be told from a bare CID.
 * When an optimized Pinata URL fails, the original is tried before anything else happens. Once
 * it has loaded, the file is remembered for the session, so the next time this element (or any
 * other) is given it, the original is used straight away.
 *
 * Most of these images also have the component's own `(error)` handler, which swaps in a
 * placeholder. The error listener here runs in the capture phase - ahead of that handler on the
 * element itself - and stops it only while the original is being tried. If the original fails
 * too, the component's handler runs as before. An original that has already failed on this
 * element is not retried again, so nothing can loop.
 *
 * Listeners and the src watcher run outside Angular: a table can hold many of these, and each
 * change only needs a class toggle or a src swap, not a change-detection pass.
 *
 * Usage:
 *   <img appNftImage [src]="getPinataThumbnailUrl(nft.primary_media, 88)"
 *        (error)="onImageError($event)">
 */
@Directive({
  selector: 'img[appNftImage]',
  standalone: false
})
export class NftImageDirective implements OnInit, OnDestroy {

  /** The original URL currently being tried after its optimized form failed. */
  private pendingOriginal: string | null = null;

  /** An original that has already failed on this element, so it is not retried again. */
  private brokenOriginal: string | null = null;

  private srcObserver: MutationObserver | null = null;

  private readonly onLoad = (): void => {
    this.stopShimmer();
    if (this.pendingOriginal && this.currentSrc() === this.pendingOriginal) {
      markPinataUnoptimizable(this.pendingOriginal);
    }
    this.pendingOriginal = null;
  };

  private readonly onError = (event: Event): void => {
    const img = this.host.nativeElement;
    const current = this.currentSrc();
    const original = stripPinataImageParams(current);

    if (typeof original === 'string' && original !== current && original !== this.brokenOriginal) {
      // Keep the component's placeholder handler out of it while the original is tried.
      event.stopImmediatePropagation();
      this.pendingOriginal = original;
      img.src = original;
      return;
    }

    if (current && current === this.pendingOriginal) {
      // The retry itself failed: the file is unreachable, not just unoptimizable.
      this.brokenOriginal = current;
    }
    this.pendingOriginal = null;
    this.stopShimmer();
  };

  constructor(
    private readonly host: ElementRef<HTMLImageElement>,
    private readonly zone: NgZone
  ) { }

  ngOnInit(): void {
    const img = this.host.nativeElement;

    this.zone.runOutsideAngular(() => {
      img.addEventListener('load', this.onLoad);
      img.addEventListener('error', this.onError, { capture: true });
      if (typeof MutationObserver !== 'undefined') {
        this.srcObserver = new MutationObserver(() => this.onSrcChange());
        this.srcObserver.observe(img, { attributes: true, attributeFilter: ['src'] });
      }
    });

    // A static src is already in place before this runs; a bound one arrives later and is
    // picked up by the observer.
    this.onSrcChange();
  }

  ngOnDestroy(): void {
    this.srcObserver?.disconnect();
    const img = this.host.nativeElement;
    img.removeEventListener('load', this.onLoad);
    img.removeEventListener('error', this.onError, { capture: true });
  }

  /**
   * Reacts to a new src: goes straight to the original for a file the optimizer is known to
   * refuse, and shows the shimmer until the new image has loaded.
   */
  private onSrcChange(): void {
    const img = this.host.nativeElement;
    const src = this.currentSrc();

    if (!src) {
      this.stopShimmer();
      return;
    }

    const original = stripPinataImageParams(src);
    if (typeof original === 'string' && original !== src && isPinataUnoptimizable(src)) {
      // Changing src re-runs this for the original.
      img.src = original;
      return;
    }

    if (img.complete && img.naturalWidth > 0) {
      this.stopShimmer();
      return;
    }
    img.classList.add(IMAGE_SHIMMER_CLASS);
  }

  private currentSrc(): string {
    return (this.host.nativeElement.getAttribute('src') || '').trim();
  }

  private stopShimmer(): void {
    this.host.nativeElement.classList.remove(IMAGE_SHIMMER_CLASS);
  }
}
